<template>
  <app-link
    :to="path"
    class="event-card">
    <div class="event-card__body"  :style="{
      '--ratio': $options.ratio,
      '--card-ratio': $options.cardRatio,
    }">
      <template v-if="isTwoParticipant">
        <div class="event-card__first"
             :style="{'--color': teams.awayTeam.color}"
        >
          <div class="event-card__category" :title="sport?.name" v-if="sport">
            <SportIcons
              v-if="sport"
              :sport="sport"
              mask="#fff"
              class="event-card__category-image"
            />
            <p>{{sport?.name}} {{fullGender ? `- ${$t(fullGender)}` : ''}}</p>
          </div>
          <div class="event-card__image-wrapper" :class="{
            'padding': hasAccess,
            'margin-top': !sport,
          }">
            <image-component
              :path="teams?.awayTeam?.img"
              :alt="teams?.awayTeam?.name"
            />
          </div>
          <event-button v-if='!hasAccess' :event="event" class="button d-flex align-items-center"/>
        </div>
        <div class="event-card__second"
             :class="{'padding': !isPlayOff}"
             :style="{'--color': teams?.homeTeam?.color}"
        >
          <div v-if="isPlayOff" class="event-card__playoff"><p>PLAYOFF</p></div>
          <div class="event-card__image-wrapper">
            <image-component
              :path="teams?.homeTeam?.img"
              :alt="teams?.homeTeam?.name"
            />
          </div>
          <p class="event-card__category event-card__time" :title="eventTime">
            {{eventTime}}
          </p>
        </div>
      </template>
      <template v-else>
        <div class="event-card__poster">
          <div
            v-if="sport || isPlayOff"
            class="event-card__category d-flex align-items-center pl-1"
            :title="sport?.name"
          >
            <SportIcons v-if="sport" :sport="sport" mask="#fff" class="event-card__category-image"/>
            <p class="flex-grow-1">{{ sport?.name }} {{fullGender ? `- ${fullGender}` : ''}}</p>
            <div v-if="isPlayOff" class="event-card__playoff">PLAYOFF</div>
          </div>
          <div class="event-card__poster-wrapper">
            <image-component :path="eventPoster" :alt="event?.name"/>
          </div>
          <div class="d-flex">
            <event-button
              v-if='!hasAccess'
              :event="event"
              class="button flex-grow-1 text-center d-flex align-items-center"
            />
            <p class="event-card__category event-card__time flex-grow-1" :title="eventTime">
              {{ eventTime }}
            </p>
          </div>
        </div>
      </template>
    </div>
    <div class="event-card__content">
      <div class="event-card__statuses">
        <event-status :event="event"/>
      </div>
      <div class="event-card__data">
        <div class="d-flex gap-1">
          <p>{{ title }}</p>
        </div>
        <p v-if="message" class="event-card__sub-title">{{ message }}</p>
        <p v-if="venue" class="event-card__sub-title">{{ venue }}</p>
      </div>
      <div v-if="!hasAccess" class="event-card__access">
        <account-lock/>
      </div>
    </div>
  </app-link>
</template>
<script>
import EventCard from './EventCard';
import AppLink from '@/components/base-components/AppLink.vue';
import {CARD_RATIO, IMAGE_RATIO} from '@/const';

export default {
  name: 'EventCard_v2',
  components: {AppLink},
  extends: EventCard,
  ratio: IMAGE_RATIO,
  cardRatio: CARD_RATIO,
};
</script>
<style scoped lang="scss">
@import "src/assets/css/mixins";

.button{
  text-align: left;
  text-decoration: none;
  font-weight: 400;
  transition: font-weight .2s linear;
  font-size: var(--card-font-size);
  white-space: nowrap;
  margin: 0;
  line-height: var(--bottom-text-line-height);
  padding: .2rem var(--button-padding-x);
  &:hover {
    font-weight: 800;
  }
}
.event-card {
  --card-font-size: clamp(.7rem, .75vw, .8rem);
  color: var(--settings-event-card-text-color);
  display: flex;
  flex-direction: column;
  text-decoration: none;
  font-style: normal;

  &:hover {
    overflow: visible;
    transition: all .1s linear;
    transform: scale(1.05);
  }
}

.event-card__body {
  --image-wrapper-padding: 2rem;
  --button-padding-x: .55rem;
  --w1: calc(var(--button-padding-x) + .8rem + .5rem + 10ch + var(--button-padding-x) + 2rem);
  --w2: calc(100% - var(--w1));
  --bottom-font-size: 0.65rem;
  --bottom-text-line-height: 1.5rem;
  border-radius: 0.3rem;
  color: var(--settings-event-card-text-color-accent);
  overflow: hidden;
  position: relative;
  font-size: var(--bottom-font-size);
  @include ratio(var(--card-ratio));
  .event-card__playoff {
    padding: 0.3rem;
    text-align: right;
    background: var(--theme-accent);
    color: var(--settings-body-text-color);
    line-height: 1;
    font-size: .8rem;
    font-weight: 400;
  }

  p {
    margin: 0;
    line-height: var(--bottom-text-line-height);
    padding: .2rem var(--button-padding-x);
  }

  .event-card__category {
    background: var(--settings-event-card-background-color-accent);
    font-size: var(--card-font-size);
    font-weight: 400;
    white-space: nowrap;
  }

  img {
    height: 50%;
    object-position: center;
  }
}

.event-card__first {
  display: flex;
  flex-direction: column;
  background: var(--color);
  width: 100%;
  height: 100%;
  clip-path: polygon(0 0, 0 100%, var(--w1) 100%, var(--w2) 0);
  shape-outside: polygon(0 0, 0 100%, var(--w1) 100%, var(--w2) 0);

  .event-card__category {
    display: grid;
    grid-template-columns: 2rem 1fr;
    text-align: left;
    align-items: center;
    width: calc(1rem + var(--w2));
    padding: .3rem;

    p {
      height: 1lh;
      width: 100%;
      @include line-clamp(1);
    }

    img {
      width: 1.5rem;
      height: 1.5rem;
    }

    .event-card__category-title {
      width: max-content;
      overflow: hidden;
    }
    @keyframes move {
      0% {
        margin-left: 0;
      }
      50% {
        margin-left: -100%;
      }
      50.01% {
        margin-left: 100%;
      }
      100% {
        margin-left: 0
      }
    }
    &:hover {
      .event-card__category-title div {
        animation: move 6s linear infinite;

        p {
          overflow: inherit;
          text-overflow: inherit;
          display: inline-block;
          white-space: nowrap;
        }
      }
    }

  }
}

.event-card__category-image {
  width: 1.5rem;
  height: 1.5rem;
}

.event-card__second {
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background: var(--color);
  shape-outside: polygon(var(--w2) 0, var(--w1) 100%, 100% 100%, 100% 0);
  clip-path: polygon(var(--w2) 0, var(--w1) 100%, 100% 100%, 100% 0);
  &.padding {
    padding-top: 1.5rem;
  }

  p {
    text-align: right;
    margin-left: auto;
    width: calc(1rem + var(--w2));
    padding-left: 1rem;
  }

  .event-card__image-wrapper {
    width: 35%;
    margin-left: auto;
    justify-content: flex-end;
    padding-left: 0;
    padding-right: var(--image-wrapper-padding);
  }
}

.event-card__image-wrapper {
  width: 33%;
  display: flex;
  align-items: center;
  flex-grow: 1;
  justify-content: flex-start;
  padding-left: var(--image-wrapper-padding);
  &.padding {
    padding-bottom: 2rem;
  }

  &.margin-top {
    margin-top: calc(1lh + 2 * .3rem);
  }

  img {
    height: auto;
    width: 100%;
    object-fit: contain;
  }
}

.event-card__poster {
  height: 100%;
  display: flex;
  flex-direction: column;

  .event-card__category {
    padding: .3rem;

    p {
      height: 1lh;
    }
  }
}

.event-card__poster-wrapper {
  position: relative;
  flex-grow: 1;

  img {
    position: absolute;
    top: 0;
    height: 100%;
    left: 0;
    width: 100%;
    object-fit: cover;
  }
}

@include media-max(xs) {
  .event-card__image-wrapper {
    --image-wrapper-padding: 1rem;
  }
}

.event-card__content {
  display: flex;
  gap: .5rem;
  padding: 1rem 0;

  p {
    margin: 0;
    text-align: left;
  }
}

.event-card__statuses {
  display: flex;
  flex-direction: column;
  gap: 1rem;

  svg, img {
    width: 2rem;
    height: 2rem;
  }
}

.event-card__data {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  font-size: 0.85rem;
}

.event-card__sub-title {
  font-size: 0.7rem;
  font-weight: normal;
}

.event-card__access {
  color: var(--settings-event-card-text-color);
}
</style>
